<template>
  <div class="rate-tiers-cell" :class="{ 'is-inactive': isInactive }">

    <!-- In-cell header row: Tier | Tenor | #1 #2 #3 [#4 #5 #6 at lg] -->
    <div class="rtc-grid rtc-header">
      <span class="rtc-col-tier">Tier</span>
      <span class="rtc-col-tenor">Tenor</span>
      <span class="rtc-col-rate rtc-rate-header">#1</span>
      <span class="rtc-col-rate rtc-rate-header">#2</span>
      <span class="rtc-col-rate rtc-rate-header">#3</span>
      <span class="rtc-col-rate rtc-rate-header rtc-lg-only">#4</span>
      <span class="rtc-col-rate rtc-rate-header rtc-lg-only">#5</span>
      <span class="rtc-col-rate rtc-rate-header rtc-lg-only">#6</span>
    </div>

    <!-- Tier rows (capped at MAX_VISIBLE_TIERS=5; scrollable when overflow) -->
    <div class="rtc-tiers-region" :class="{ 'rtc-scrollable': hasOverflow }">
      <div
        v-for="tier in visibleTiers"
        :key="tier.seqNo"
        class="rtc-grid rtc-tier-row">

        <span class="rtc-col-tier">
          <span class="tier-tag" :class="tier.seqNo === 1 ? 'tier-1' : 'tier-n'">T{{ tier.seqNo }}</span>
        </span>

        <span class="rtc-col-tenor">{{ tenorLabel(tier) }}</span>

        <span class="rtc-col-rate r1">{{ formatRate(tier.telco1) }}</span>
        <span class="rtc-col-rate">{{ formatRate(tier.telco2) }}</span>
        <span class="rtc-col-rate">{{ formatRate(tier.telco3) }}</span>
        <span class="rtc-col-rate rtc-lg-only">{{ formatRate(tier.telco4) }}</span>
        <span class="rtc-col-rate rtc-lg-only">{{ formatRate(tier.telco5) }}</span>
        <span class="rtc-col-rate rtc-lg-only">{{ formatRate(tier.telco6) }}</span>

      </div>
    </div>

    <!-- Overflow note (>5 tiers) -->
    <div v-if="hasOverflow && !hideDrawerAffordance" class="rtc-overflow-note">
      Tiers T6&#8211;T{{ lastTierSeqNo }} in drawer
    </div>

    <!-- +4 more in drawer (rates #7–#10 are always drawer-only) -->
    <div v-if="tiers.length > 0 && !hideDrawerAffordance" class="rtc-more-rates">
      +4 more in drawer
    </div>

  </div>
</template>

<script>
var MAX_VISIBLE_TIERS = 5

export default {
  name: 'rate-tiers-cell',

  props: {
    rowData: {
      type: Object,
      required: true
    },

    hideDrawerAffordance: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    // Derive normalized tiers from rowData — computed so prop changes re-render
    // (row-recycle guard: el-table reuses cells across pagination/sort)
    tiers: function () {
      var row = this.rowData
      if (!row) return []

      var tiersArr = row.tiers
      if (tiersArr && Array.isArray(tiersArr) && tiersArr.length > 0) {
        return tiersArr.slice().sort(function (a, b) {
          return Number(a.seqNo) - Number(b.seqNo)
        }).map(function (t) {
          return {
            seqNo: Number(t.seqNo),
            fromMonth: Number(t.fromMonth) || 0,
            toMonth: Number(t.toMonth) || 0,
            telco1: Number(t.telco1) || 0,
            telco2: Number(t.telco2) || 0,
            telco3: Number(t.telco3) || 0,
            telco4: Number(t.telco4) || 0,
            telco5: Number(t.telco5) || 0,
            telco6: Number(t.telco6) || 0
          }
        })
      }

      // Flat fallback: synthesize a single Tier 1 from flat root columns
      return [{
        seqNo: 1,
        fromMonth: Number(row.fromMonth) || 0,
        toMonth: Number(row.toMonth) || 0,
        telco1: Number(row.telco1) || 0,
        telco2: Number(row.telco2) || 0,
        telco3: Number(row.telco3) || 0,
        telco4: Number(row.telco4) || 0,
        telco5: Number(row.telco5) || 0,
        telco6: Number(row.telco6) || 0
      }]
    },

    visibleTiers: function () {
      return this.tiers.slice(0, MAX_VISIBLE_TIERS)
    },

    hasOverflow: function () {
      return this.tiers.length > MAX_VISIBLE_TIERS
    },

    lastTierSeqNo: function () {
      if (this.tiers.length === 0) return 0
      return this.tiers[this.tiers.length - 1].seqNo
    },

    isInactive: function () {
      return this.rowData ? this.rowData.status !== 'Active' : false
    }
  },

  methods: {
    tenorLabel: function (tier) {
      var from = Number(tier.fromMonth) || 0
      var to = Number(tier.toMonth) || 0
      var diff = Math.max(0, to - from)
      var years = (diff / 12).toFixed(1)
      // en-dash U+2013 between months; spaced middot U+00B7 between groups
      return from + '–' + to + ' mo · ' + years + ' yr'
    },

    formatRate: function (value) {
      var n = Number(value)
      if (isNaN(n) || n === 0) return '–'
      return n.toLocaleString('en-US', { maximumFractionDigits: 0 })
    }
  }
}
</script>

<style scoped>
.rate-tiers-cell {
  font-size: 12px;
  line-height: 1.5;
  min-width: 0;
}

/* Base grid: Tier | Tenor | #1 #2 #3 (lg-only cols are display:none at md/sm).
   Tier + Tenor are FIXED widths (not auto) so the header row and every tier row —
   which are independent grids — share identical track geometry; otherwise the
   header's short "Tenor" vs a row's long tenor label give the 1fr rate columns
   different widths and the #N headers drift off their values (XOL-319). */
.rtc-grid {
  display: grid;
  grid-template-columns: 32px 116px repeat(3, 1fr);
  gap: 0 4px;
  align-items: baseline;
}

/* Header row */
.rtc-header {
  color: #8492a6;
  font-size: 11px;
  margin-bottom: 3px;
  border-bottom: 1px solid #ebeef5;
  padding-bottom: 2px;
}

.rtc-rate-header {
  font-style: italic;
  text-align: right;
}

/* Tier tag tokens (mirror SharerRateTierCard.vue left-bar tokens) */
.tier-tag {
  display: inline-block;
  padding: 1px 5px;
  border-radius: 3px;
  font-size: 11px;
  font-weight: 500;
  line-height: 1.4;
  white-space: nowrap;
}

.tier-1 {
  background: #f0f9eb;
  color: #67c23a;
}

.tier-n {
  background: #f4f4f5;
  color: #909399;
}

/* Column types */
.rtc-col-tier {
  white-space: nowrap;
}

.rtc-col-tenor {
  white-space: nowrap;
  padding-right: 6px;
  color: #606266;
  font-size: 11px;
}

.rtc-col-rate {
  text-align: right;
  font-variant-numeric: tabular-nums;
  color: #303133;
  font-size: 12px;
}

/* Rate #1 — slightly bolder */
.r1 {
  font-weight: 500;
}

/* lg-only columns: #4 #5 #6 — shown only at ≥1200px */
.rtc-lg-only {
  display: none;
}

@media (min-width: 1200px) {
  .rtc-grid {
    grid-template-columns: 32px 116px repeat(6, 1fr);
  }

  .rtc-lg-only {
    display: block; /* reveal #4 #5 #6 header + cells */
  }
}

/* Scrollable tiers region when >5 tiers */
.rtc-tiers-region.rtc-scrollable {
  max-height: 200px;
  overflow-y: auto;
}

/* Overflow note: "Tiers T6–T{last} in drawer" */
.rtc-overflow-note {
  text-align: right;
  color: #8492a6;
  font-size: 11px;
  margin-top: 3px;
}

/* "+N more in drawer" */
.rtc-more-rates {
  text-align: right;
  color: #8492a6;
  font-size: 11px;
  margin-top: 2px;
}

/* Inactive: grey the cell (can't reach tr — greys own td only) */
.is-inactive .rtc-col-tenor,
.is-inactive .rtc-col-rate,
.is-inactive .rtc-col-tier,
.is-inactive .rtc-header,
.is-inactive .rtc-overflow-note,
.is-inactive .rtc-more-rates {
  color: #8492a6 !important;
}

.is-inactive .tier-1,
.is-inactive .tier-n {
  background: #f4f4f5;
  color: #8492a6 !important;
}
</style>
